/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import type { RendererProps } from './types'

import { byState } from '../diff/normalize'
import { ChangeList, NoChangesPanel, Section } from '../primitives'

/**
 * The fallback renderer, and the most important one to get right.
 *
 * Every entity type that has no purpose-built renderer gets this — including entity types the
 * backend adds after this build ships. It must therefore look deliberate rather than broken:
 * humanised paths, type-aware values, and changes grouped into added / modified / removed.
 */
export const GenericDiffRenderer: React.FC<RendererProps> = ({ changes }) => {
  // Also guarded by the dispatcher; kept so this renderer is safe to use on its own.
  if (changes.length === 0) return <NoChangesPanel />

  const groups = byState(changes)
  const sections = [
    { title: 'Added', changes: groups.ADDED },
    { title: 'Modified', changes: groups.MODIFIED },
    { title: 'Removed', changes: groups.REMOVED },
  ].filter((section) => section.changes.length > 0)

  return (
    <div className="flex flex-col gap-3">
      {/* A single-bucket diff needs no grouping — the headers would be pure noise. */}
      {sections.length === 1 ? (
        <ChangeList changes={sections[0].changes} />
      ) : (
        sections.map((section) => (
          <Section key={section.title} title={section.title} count={section.changes.length}>
            <ChangeList changes={section.changes} />
          </Section>
        ))
      )}
    </div>
  )
}
